import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { ArrowSquareOut, PencilSimple } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { useCareProviderWithOrigin } from "@/modules/care-provider/services/useCareProviderWithOrigin"
import { combineCareProviderTitle } from "@/modules/care-provider/utils/combine-care-provider-title"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { ChNutritionalCounsellingDrawer } from "@/modules/patients/detail/therapy-prescription/ch/ch-nutritional-counselling-drawer"
import { chNutritionalCounsellingFragment$key } from "@/modules/patients/detail/therapy-prescription/ch/services/__generated__/chNutritionalCounsellingFragment.graphql"
import { chNutritionalCounsellingFragment } from "@/modules/patients/detail/therapy-prescription/ch/services/chNutritionalCounsellingFragment"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface NutritionalCounsellingDocumentProps extends DocumentProps {
  nutritionalCounsellingRef: chNutritionalCounsellingFragment$key
  progressNoteId: string
  isEditable?: boolean
  healthcareProviderId: string
}

export const NutritionIcon = ({ isSelected }: { isSelected?: boolean }) => {
  return (
    <svg viewBox="0 0 24 25" xmlns="http://www.w3.org/2000/svg" width="24" height="24" className="h-6 w-6 shrink-0">
      <path
        d="M12 20.9999C10.191 20.9999 8.65544 20.3688 7.39327 19.1066C6.13109 17.8445 5.5 16.3089 5.5 14.4999C5.5 13.0294 5.93847 11.715 6.8154 10.5567C7.69232 9.39836 8.8391 8.62497 10.2557 8.23652C9.92242 8.1596 9.59582 8.04518 9.27595 7.89324C8.95608 7.74133 8.67435 7.54357 8.43075 7.29997C7.93205 6.80127 7.60994 6.20961 7.46442 5.52499C7.31891 4.84038 7.27179 4.14487 7.32308 3.43847C8.01666 3.38718 8.70095 3.4327 9.37595 3.57502C10.0509 3.71732 10.6378 4.03142 11.1365 4.51732C11.5198 4.88142 11.7958 5.30353 11.9644 5.78364C12.133 6.26378 12.2391 6.7673 12.2826 7.29422C12.4929 6.79038 12.7474 6.31411 13.0461 5.86539C13.3448 5.41668 13.6859 5.00065 14.0692 4.61732C14.2076 4.47885 14.3833 4.40962 14.5961 4.40962C14.8089 4.40962 14.9846 4.47885 15.123 4.61732C15.2615 4.75577 15.3307 4.9314 15.3307 5.14422C15.3307 5.35703 15.2615 5.53268 15.123 5.67114C14.7628 6.03139 14.4442 6.42433 14.1672 6.84997C13.8903 7.27562 13.6756 7.72883 13.523 8.20959C14.9897 8.55446 16.1858 9.31214 17.1115 10.4826C18.0371 11.6532 18.5 12.9923 18.5 14.4999C18.5 16.3089 17.8689 17.8445 16.6067 19.1066C15.3445 20.3688 13.8089 20.9999 12 20.9999ZM12 19.4999C13.3833 19.4999 14.5625 19.0124 15.5375 18.0374C16.5125 17.0624 17 15.8833 17 14.4999C17 13.1166 16.5125 11.9374 15.5375 10.9624C14.5625 9.98744 13.3833 9.49994 12 9.49994C10.6166 9.49994 9.43747 9.98744 8.46247 10.9624C7.48747 11.9374 6.99997 13.1166 6.99997 14.4999C6.99997 15.8833 7.48747 17.0624 8.46247 18.0374C9.43747 19.0124 10.6166 19.4999 12 19.4999Z"
        className={isSelected ? "fill-on-primary-surface" : "fill-on-surface-subdued"}
      />
    </svg>
  )
}

const NutritionalCounsellingDocument = ({
  nutritionalCounsellingRef,
  progressNoteId,
  healthcareProviderId,
  isEditable = true,
  isConclusion,
  isSigned,
  isSent,
  onSelect,
}: NutritionalCounsellingDocumentProps) => {
  const { t } = useTranslation()
  const { id, treatmentUnit } = useFragment(chNutritionalCounsellingFragment, nutritionalCounsellingRef) ?? {}
  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const isSelected = isConclusion && selectedDocumentId === id

  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)

  const careProvider = useCareProviderWithOrigin(healthcareProviderId)

  const heathcareProviderName = combineCareProviderTitle({
    businessName: careProvider?.businessName,
    title: careProvider?.title,
    firstName: careProvider?.firstName,
    lastName: careProvider?.lastName,
  })

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered relative flex items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="nutritional-counselling-document-item"
      onClick={() => {
        if (!isEditable) {
          onSelect()
          return
        }
        openDrawer(
          () => (
            <ChNutritionalCounsellingDrawer
              progressNoteId={progressNoteId}
              nutritionalCounsellingRef={nutritionalCounsellingRef}
              id={id}
            />
          ),
          "NutritionalCounsellingDrawer",
        )
      }}>
      <NutritionIcon isSelected={isSelected} />
      <div className="ml-2 min-w-0">
        <ListText className={isSelected ? "text-on-primary-surface" : ""}>
          {t("episodeOfCare.progressNote.therapyPrescriptionNutritionalCounselling")}
        </ListText>
        <ListText
          data-testid="nutritional-counselling-details"
          variant="secondary"
          className={cn("truncate", isSelected && "text-on-primary-surface")}>
          {joinStrings([heathcareProviderName, `${t("patient.therapyPrescription.units")}: ${treatmentUnit}`])}
        </ListText>
      </div>
      {isEditable && (
        <div className="absolute right-2 ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" data-testid="nutritional-counselling-edit-button" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="absolute right-2 ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut
              className="text-on-surface-subdued"
              data-testid="nutritional-counselling-view-document-button"
            />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag
          className="ml-auto group-hover:hidden"
          type={isSigned ? "success" : "critical"}
          label={isSigned ? t("patient.conclude.signed") : t("patient.conclude.signThis")}
        />
      )}
    </ListItem>
  )
}

export { NutritionalCounsellingDocument }
